function getAuthHeaders() {
  const user = JSON.parse(sessionStorage.getItem('user') || 'null');
  if (!user || !user.signInUserSession) {
    throw new Error('User not authenticated');
  }
  return {
    Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}`,
    'Content-Type': 'application/json',
  };
}

function getAuthHeadersNoJson() {
  const headers = getAuthHeaders();
  delete headers['Content-Type'];
  return headers;
}

function baseUrl() {
  return process.env.REACT_APP_BASEURL || '';
}

export function isRetail() {
  const v = localStorage.getItem('isRetail');
  return ![false, 'false', undefined, 'undefined', null, 'null', ''].includes(v);
}

function timezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

async function parseMaybeJson(res) {
  const text = await res.text();
  if (!text) return null;
  let parsed;
  try {
    parsed = JSON.parse(text);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
  } catch (e) {
    throw new Error('Invalid JSON response');
  }
  return parsed;
}

export async function fetchDashlate({ fromdate, todate, id, deliverytype }) {
  const res = await fetch(`${baseUrl()}dashlate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      fromdate,
      todate,
      id: String(id),
      deliverytype,
      timezone: timezone(),
    }),
  });
  if (!res.ok) throw new Error(`dashlate HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** Utilization "Pick Ups" drill-down — same as Limitless pickuptransactions/:period/:deliverytype */
export async function fetchPickupTransactions({ period, deliverytype }) {
  const res = await fetch(
    `${baseUrl()}pickuptransactions/${encodeURIComponent(period)}/${encodeURIComponent(
      deliverytype || 'all'
    )}`,
    { method: 'GET', headers: getAuthHeadersNoJson() }
  );
  if (!res.ok) throw new Error(`pickuptransactions HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchSales({ fromdate, todate, id }) {
  const res = await fetch(`${baseUrl()}sales`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      fromdate,
      todate,
      id: String(id),
      timezone: timezone(),
    }),
  });
  if (!res.ok) throw new Error(`sales HTTP ${res.status}`);
  return parseMaybeJson(res);
}

export async function fetchDashlateDays({ fromdate, todate }) {
  const path = isRetail() ? 'apis/i-ecom/status/dashlateDays' : 'dashlateDays';
  const res = await fetch(`${baseUrl()}${path}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ fromdate, todate, timezone: timezone() }),
  });
  if (!res.ok) throw new Error(`dashlateDays HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchDashlateTime({ fromdate, todate }) {
  const path = isRetail() ? 'apis/i-ecom/status/dashlateTime' : 'dashlateTime';
  const res = await fetch(`${baseUrl()}${path}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ fromdate, todate, timezone: timezone() }),
  });
  if (!res.ok) throw new Error(`dashlateTime HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchBoxList({ pageNo = 1, pageSize = 20, searchtext = '' }) {
  const body = { pageNo, pageSize };
  if (searchtext) body.searchtext = searchtext;
  const res = await fetch(`${baseUrl()}BoxList`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`BoxList HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** SSRM-friendly BoxList page: { results, total } */
export async function fetchBoxListPage(body) {
  const rows = await fetchBoxList({
    pageNo: body.pageNo,
    pageSize: body.pageSize,
    searchtext: body.searchtext || '',
  });
  const sorted = [...rows].sort((a, b) =>
    a.index > b.index ? 1 : b.index > a.index ? -1 : 0
  );
  const total =
    (sorted[0] && sorted[0].totalRecords != null
      ? Number(sorted[0].totalRecords)
      : sorted.length) || 0;
  return { results: sorted, total };
}

export async function fetchLiveAgents({ pageNo = 1, pageSize = 25 }) {
  const res = await fetch(`${baseUrl()}LiveAgents`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      pageNo,
      pageSize,
      parentAccountId: sessionStorage.getItem('parentAccountId'),
    }),
  });
  if (!res.ok) throw new Error(`LiveAgents HTTP ${res.status}`);
  const data = await res.json();
  return {
    results: (data && data.results) || [],
    total: (data && data.total) || 0,
  };
}

function liveAgentMatchesSearch(row, searchtext) {
  if (!searchtext) return true;
  const q = String(searchtext).toLowerCase();
  const hay = [
    row.account,
    row.name,
    row.is_user_consult ? 'yes' : 'no',
    row.last_login_on,
  ]
    .filter((v) => v != null && v !== '')
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

/**
 * SSRM page for Live Agents. When searchtext is set (column filters folded in),
 * load a larger window and filter/paginate client-side — LiveAgents has no search API.
 */
export async function fetchLiveAgentsPage(body) {
  const pageSize = body.pageSize || 25;
  const pageNo = body.pageNo || 1;
  const searchtext = (body.searchtext || '').trim();

  if (!searchtext) {
    return fetchLiveAgents({ pageNo, pageSize });
  }

  const all = await fetchLiveAgents({ pageNo: 1, pageSize: 5000 });
  const filtered = (all.results || []).filter((row) =>
    liveAgentMatchesSearch(row, searchtext)
  );
  const start = (pageNo - 1) * pageSize;
  return {
    results: filtered.slice(start, start + pageSize),
    total: filtered.length,
  };
}

const detailsInflight = {};

export async function fetchTaskManagerDetails(body) {
  const key = JSON.stringify(body || {});
  if (detailsInflight[key]) {
    return detailsInflight[key];
  }
  detailsInflight[key] = (async () => {
    try {
      const res = await fetch(`${baseUrl()}taskmanager/details`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`taskmanager/details HTTP ${res.status}`);
      const data = await res.json();
      const page = data && data.results;
      return {
        results: (page && page.results) || [],
        total: (page && page.total) || 0,
      };
    } finally {
      delete detailsInflight[key];
    }
  })();
  return detailsInflight[key];
}

export async function fetchAverageTurnOver({ fromdate, todate }) {
  const res = await fetch(`${baseUrl()}getAverageTurnOver`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ fromdate, todate, timezone: timezone() }),
  });
  if (!res.ok) throw new Error(`getAverageTurnOver HTTP ${res.status}`);
  return parseMaybeJson(res);
}

export async function fetchAverageTurnOverReport({ fromdate, todate, period }) {
  const res = await fetch(
    `${baseUrl()}AverageTurnOverReport/${encodeURIComponent(fromdate)}/${encodeURIComponent(
      todate
    )}/${period}`,
    { method: 'GET', headers: getAuthHeadersNoJson() }
  );
  if (!res.ok) throw new Error(`AverageTurnOverReport HTTP ${res.status}`);
  const data = await res.json();
  return (data && data.results) || [];
}

export async function fetchTransactionTime({ fromdate, todate }) {
  const res = await fetch(`${baseUrl()}getTransactionTime`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ fromdate, todate, timezone: timezone() }),
  });
  if (!res.ok) throw new Error(`getTransactionTime HTTP ${res.status}`);
  const data = await parseMaybeJson(res);
  if (!data || data.success !== true || data.metricVersion !== 2) throw new Error('Pickup metric response unavailable');
  return data;
}

export async function fetchAverageTransactionTimeReport({ fromdate, todate, period }) {
  const res = await fetch(
    `${baseUrl()}AverageTransactionTimeReport/${encodeURIComponent(
      fromdate
    )}/${encodeURIComponent(todate)}/${period}?timezone=${encodeURIComponent(timezone())}`,
    { method: 'GET', headers: getAuthHeadersNoJson() }
  );
  if (!res.ok) throw new Error(`AverageTransactionTimeReport HTTP ${res.status}`);
  const data = await res.json();
  return (data && data.results) || [];
}

export async function fetchAdherenceAndRTS({ fromdate, todate }) {
  const res = await fetch(`${baseUrl()}AdherenceAndRTSDashlate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ fromdate, todate, timezone: timezone() }),
  });
  if (!res.ok) throw new Error(`AdherenceAndRTSDashlate HTTP ${res.status}`);
  const data = await res.json();
  return (data && data.result) || [];
}

export async function fetchAdherenceAndRTSReport({ fromdate, todate }) {
  const res = await fetch(
    `${baseUrl()}AdherenceAndRTSReport/${encodeURIComponent(fromdate)}/${encodeURIComponent(
      todate
    )}`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) throw new Error(`AdherenceAndRTSReport HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchConsultationsDashboard({ fromdate, todate }) {
  const res = await fetch(`${baseUrl()}getConsultationsDashboard`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ fromdate, todate, timezone: timezone() }),
  });
  if (!res.ok) throw new Error(`getConsultationsDashboard HTTP ${res.status}`);
  return res.json();
}

export async function fetchConsultationsReports({
  date,
  searchtxt = '',
  page = 1,
  pageSize = 25,
  status = 'Report',
}) {
  const res = await fetch(`${baseUrl()}getConsultationsReports`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ date, searchtxt, page, pageSize, status }),
  });
  if (!res.ok) throw new Error(`getConsultationsReports HTTP ${res.status}`);
  const data = await res.json();
  return {
    results: (data && data.results) || [],
    total: (data && data.total) || (data && data.results && data.results.length) || 0,
  };
}

export async function fetchPatientDetails({ fromdate, todate, id }) {
  const res = await fetch(`${baseUrl()}PatientDetails`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      fromdate,
      todate,
      id: String(id),
      ...(Number(id) === 4 ? { groupBy: 'month' } : {}),
      timezone: timezone(),
    }),
  });
  if (!res.ok) throw new Error(`PatientDetails HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchPatientEnrolledCount() {
  const res = await fetch(`${baseUrl()}PatientEnrolledCount`, {
    method: 'GET',
    headers: getAuthHeadersNoJson(),
  });
  if (!res.ok) throw new Error(`PatientEnrolledCount HTTP ${res.status}`);
  const data = await res.json();
  return (data && data.count) || 0;
}

export async function fetchBoxOrdersChart({ sinceDays }) {
  const qs = new URLSearchParams({
    timezone: timezone(),
    sinceDays: String(sinceDays),
    groupBy: 'date',
  });
  const res = await fetch(`${baseUrl()}reports/boxUtilization/orders?${qs}`, {
    method: 'GET',
    headers: getAuthHeadersNoJson(),
  });
  if (!res.ok) throw new Error(`boxUtilization/orders HTTP ${res.status}`);
  return res.json();
}

/**
 * Objection .page() returns { results, total }; disablePagination returns a bare array.
 */
export function normalizeDetailRows(detail) {
  if (Array.isArray(detail)) return detail;
  if (detail && Array.isArray(detail.results)) return detail.results;
  return [];
}

export async function fetchBoxUtilizationReport({
  sinceDays,
  limit = 10,
  currentPagePharmacy = 1,
  currentPageOptical = 1,
  disablePagination = false,
}) {
  const body = new URLSearchParams({
    timezone: timezone(),
    sinceDays: String(sinceDays),
    limit: String(limit),
    currentPagePharmacy: String(currentPagePharmacy),
    currentPageOptical: String(currentPageOptical),
    disablePagination: String(!!disablePagination),
  });
  const res = await fetch(`${baseUrl()}reports/boxUtilization`, {
    method: 'POST',
    headers: {
      ...getAuthHeadersNoJson(),
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body: body.toString(),
  });
  if (!res.ok) throw new Error(`boxUtilization HTTP ${res.status}`);
  const data = await res.json();
  return {
    PharmacyDetail: {
      results: normalizeDetailRows(data && data.PharmacyDetail),
      total:
        (data &&
          data.PharmacyDetail &&
          !Array.isArray(data.PharmacyDetail) &&
          data.PharmacyDetail.total) ||
        normalizeDetailRows(data && data.PharmacyDetail).length,
    },
    OpticalDetail: {
      results: normalizeDetailRows(data && data.OpticalDetail),
      total:
        (data &&
          data.OpticalDetail &&
          !Array.isArray(data.OpticalDetail) &&
          data.OpticalDetail.total) ||
        normalizeDetailRows(data && data.OpticalDetail).length,
    },
  };
}

export async function fetchStockAgeing() {
  const qs = new URLSearchParams({ timezone: timezone() });
  const res = await fetch(`${baseUrl()}dashboard/stockAgeing?${qs}`, {
    method: 'GET',
    headers: getAuthHeadersNoJson(),
  });
  if (!res.ok) throw new Error(`stockAgeing HTTP ${res.status}`);
  return res.json();
}

export async function fetchStockAgeingReportPage(body, activeTab) {
  const data = await fetchStockAgeingReport({
    limit: body.pageSize,
    currentPagePharmacy: body.pageNo,
    currentPageOptical: body.pageNo,
    searchObject: body.searchObject,
    sortColumn: body.sortColumn,
    sortOrder: body.sortOrder,
  });
  return activeTab === 'optical' ? data.OpticalDetail : data.PharmacyDetail;
}

export async function fetchStockAgeingReport({
  limit = 10,
  currentPagePharmacy = 1,
  currentPageOptical = 1,
  disablePagination = false,
  searchObject,
  sortColumn,
  sortOrder,
}) {
  const body = new URLSearchParams({
    timezone: timezone(),
    limit: String(limit),
    currentPagePharmacy: String(currentPagePharmacy),
    currentPageOptical: String(currentPageOptical),
    disablePagination: String(!!disablePagination),
    searchObject: JSON.stringify(searchObject || {}),
    sortColumn: sortColumn || '',
    sortOrder: sortOrder || '',
  });
  const res = await fetch(`${baseUrl()}dashboard/stockAgeingReport`, {
    method: 'POST',
    headers: {
      ...getAuthHeadersNoJson(),
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body: body.toString(),
  });
  if (!res.ok) throw new Error(`stockAgeingReport HTTP ${res.status}`);
  const data = await res.json();
  return {
    PharmacyDetail: {
      results: normalizeDetailRows(data && data.PharmacyDetail),
      total:
        (data &&
          data.PharmacyDetail &&
          !Array.isArray(data.PharmacyDetail) &&
          data.PharmacyDetail.total) ||
        normalizeDetailRows(data && data.PharmacyDetail).length,
    },
    OpticalDetail: {
      results: normalizeDetailRows(data && data.OpticalDetail),
      total:
        (data &&
          data.OpticalDetail &&
          !Array.isArray(data.OpticalDetail) &&
          data.OpticalDetail.total) ||
        normalizeDetailRows(data && data.OpticalDetail).length,
    },
  };
}

const SUPERADMIN_ROLE_ID = 'ef8cc458-4e0d-428e-b40a-051d99a5c37c';

const ALL_PERMS = {
  weeklyTotal: true,
  sales: true,
  accounts: true,
  dailySalesStates: true,
  consultations: true,
};

export function getDashboardPermissions() {
  try {
    let superAdminId = null;
    try {
      superAdminId = JSON.parse(sessionStorage.getItem('superadmin'));
    } catch (e) {
      // ignore
    }
    if (superAdminId === SUPERADMIN_ROLE_ID) {
      return { ...ALL_PERMS };
    }

    const permissions = JSON.parse(sessionStorage.getItem('permissions') || '[]');
    if (!Array.isArray(permissions) || permissions.length === 0) {
      return { ...ALL_PERMS };
    }
    const dash = permissions.filter(
      (p) => p.permission_entity_type === 'Dashboard'
    );
    const allowed = (name) =>
      dash.some((p) => p.name && p.name.trim() === name && p.is_allowed === 1);
    if (dash.length === 0) {
      return { ...ALL_PERMS };
    }
    return {
      weeklyTotal: allowed('Weekly Total'),
      sales: allowed('Sales'),
      accounts: allowed('Accounts'),
      dailySalesStates: allowed('Daily Sales States'),
      consultations: allowed('Consultations'),
    };
  } catch (e) {
    return { ...ALL_PERMS };
  }
}



// WEBPACK FOOTER //
// ./src/components/dashboard2/dashboardApi.js